import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import { Title, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight } from "@tabler/icons-react";
import type {
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GetRowIdParams,
  ICellRendererParams,
  MenuItemDef,
  ModelUpdatedEvent,
  PaginationChangedEvent,
  RowClassParams,
  RowStyle,
  SelectionChangedEvent,
  SelectionColumnDef,
  SuppressKeyboardEventParams,
  ValueGetterParams,
} from "ag-grid-community";
import { createPart, ModuleRegistry, themeQuartz } from "ag-grid-community";
import { CellSelectionModule, ClipboardModule, ContextMenuModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { paginationPageSize, paginationPageSizeSelector } from "../../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { WarehouseOutWardSerialChildrenV2 } from "../../WarehouseOutWard/pages/warehouseOutWardSerialChildrenV2";
import { formatNumberVi, TABLE_HEAD_HEIGHT } from "../config/theme";
import { findOrderLineForRow } from "../state/selectors";
import type { SaleOrderLine, ScannedSerialRow } from "../types";

// Menu chuột phải, chọn vùng ô và copy là tính năng enterprise; đăng ký ngay tại lưới
// để không phụ thuộc màn khác đăng ký giúp (đăng ký lặp lại không sao)
ModuleRegistry.registerModules([ContextMenuModule, CellSelectionModule, ClipboardModule]);

/** Màu theo bản thiết kế */
const C = {
  text: "#1B1F24",
  textSub: "#5C6570",
  muted: "#8A929B",
  empty: "#A3AAB2",
  headBg: "#F6F7F4",
  divider: "#ECEEE9",
  rowLine: "#F0F1EE",
  footBg: "#FAFBF8",
  inputBorder: "#D5D9D2",
  accent: "#1F5FAE",
  accentBg: "#E6EEF8",
  accentBorder: "#C9D6E8",
  noSerialRow: "#F2F7FD",
  successText: "#14532F",
  successBg: "#E4F2EA",
  successBorder: "#BFDFCB",
  warnText: "#8A4B05",
  warnDashed: "#C7862A",
  childIcon: "#B26A0B",
  childBg: "#FFF8EC",
  childBorder: "#F3D3A6",
  danger: "#B42318",
  dangerBg: "#FDF1F0",
  dangerBorder: "#F2D4D1",
};

/** CSS không đặt được bằng tham số theme; AG Grid tự bọc trong class của theme nên không lan ra ngoài lưới */
const GRID_PART = createPart({
  css: `
.ag-header-cell.ssg-head-select { padding-left: 12px; }
.ag-header-cell.ssg-head-bh { padding-left: 14px; }
.ssg-head-bh .ag-header-cell-text { color: ${C.accent}; }
.ssg-head-center .ag-header-cell-label { justify-content: center; }
.ag-header-cell:not(:hover):not(:focus-within):not(.ag-header-cell-filtered) .ag-header-cell-filter-button { opacity: 0; }
.ssg-btn { transition: filter .12s ease; }
.ssg-btn:not(:disabled):hover { filter: brightness(0.96); }
.ssg-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 1px; }
`,
});

/** Tạo một lần ở cấp module; trang cũ tạo lại theme ở mỗi lần render */
const GRID_THEME = themeQuartz
  .withParams({
    fontFamily: "inherit",
    fontSize: 13,
    dataFontSize: 12.5,
    headerFontSize: 12,
    headerFontWeight: 600,
    headerTextColor: C.textSub,
    headerBackgroundColor: C.headBg,
    headerHeight: TABLE_HEAD_HEIGHT,
    rowHeight: 44,
    spacing: 6,
    cellHorizontalPadding: 8,
    iconSize: 15,
    accentColor: C.accent,
    foregroundColor: C.text,
    textColor: C.text,
    backgroundColor: "#FFFFFF",
    borderColor: C.divider,
    wrapperBorder: false,
    wrapperBorderRadius: 0,
    headerRowBorder: { style: "solid", width: 1, color: C.divider },
    rowBorder: { style: "solid", width: 1, color: C.rowLine },
    columnBorder: false,
    headerColumnBorder: false,
    pinnedColumnBorder: { style: "solid", width: 1, color: C.rowLine },
    checkboxBorderRadius: 3,
    valueChangeValueHighlightBackgroundColor: C.accentBorder,
  })
  .withPart(GRID_PART);

/** Kiểu chữ riêng từng cột */
const CELL_STYLE: Record<"index" | "sub" | "serial" | "warranty", CellStyle> = {
  index: { color: C.muted },
  sub: { color: C.textSub },
  serial: { fontWeight: "500" },
  warranty: { paddingLeft: "14px" },
};

const DEFAULT_COL_DEF: ColDef<ScannedSerialRow> = {
  editable: false,
  filter: false,
  floatingFilter: false,
  minWidth: 40,
};

/** Ô chọn nhiều dòng: đứng đầu, ghim trái như thiết kế */
const SELECTION_COLUMN_DEF: SelectionColumnDef = {
  pinned: "left",
  width: 46,
  minWidth: 46,
  maxWidth: 46,
  resizable: false,
  suppressHeaderMenuButton: true,
  headerClass: "ssg-head-select",
  cellStyle: { paddingLeft: "12px" },
};

/** Giá / thành tiền: để trống khi không có giá, như trước */
const formatMoney = (value: number | null | undefined) => (value ? formatNumberVi(value) : "");

/** Hàng không quản lý serial: theo cờ của dòng hoặc của dòng đơn cùng mã hàng */
const isNoSerialRow = (orderLines: SaleOrderLine[], row: ScannedSerialRow) =>
  row.isNoSerial === true || findOrderLineForRow(orderLines, row)?.isNoSerial === true;

/** Enter / Space khi đang đứng trên nút trong ô: để nút tự xử lý, lưới không chọn dòng */
const suppressButtonKeys = (params: SuppressKeyboardEventParams<ScannedSerialRow>) => {
  const target = params.event.target as HTMLElement | null;
  return target?.tagName === "BUTTON" && (params.event.key === "Enter" || params.event.key === " ");
};

const disabledLook = (off: boolean): React.CSSProperties => (off ? { opacity: 0.45, cursor: "not-allowed" } : {});

const S: Record<string, React.CSSProperties> = {
  wrapper: { width: "100%", display: "flex", flexDirection: "column", minHeight: 0 },
  grid: { flex: "1 1 auto", minHeight: 0 },
  cell: { display: "flex", alignItems: "center", gap: 6, height: "100%", minWidth: 0, lineHeight: "18px" },
  ellipsis: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  badge: {
    flexShrink: 0,
    padding: "1px 6px",
    borderRadius: 5,
    background: C.accentBg,
    color: C.accent,
    fontSize: 10.5,
    fontWeight: 700,
    lineHeight: "16px",
  },
  qtyCell: { display: "flex", alignItems: "center", justifyContent: "flex-end", height: "100%" },
  qtyBox: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: 40,
    height: 28,
    boxSizing: "border-box",
    padding: "0 6px",
    border: `1px solid ${C.accent}`,
    borderRadius: 6,
    background: "#FFFFFF",
    lineHeight: "normal",
    cursor: "text",
  },
  chip: {
    height: 28,
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: "0 8px",
    border: `1px solid ${C.successBorder}`,
    borderRadius: 6,
    background: C.successBg,
    color: C.successText,
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "normal",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  },
  pick: {
    height: 28,
    boxSizing: "border-box",
    padding: "0 10px",
    border: `1px dashed ${C.warnDashed}`,
    borderRadius: 6,
    background: "#FFFFFF",
    color: C.warnText,
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "normal",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    cursor: "pointer",
  },
  actions: { display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: "100%" },
  iconButton: {
    width: 28,
    height: 28,
    padding: 0,
    boxSizing: "border-box",
    borderRadius: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  footer: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "8px 14px",
    borderTop: `1px solid ${C.divider}`,
    background: C.footBg,
    color: C.textSub,
    fontSize: 12,
    lineHeight: "18px",
  },
  pager: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0, whiteSpace: "nowrap" },
  pageNav: { display: "flex", alignItems: "center", gap: 4 },
  pageButton: {
    width: 24,
    height: 24,
    padding: 0,
    boxSizing: "border-box",
    border: `1px solid ${C.inputBorder}`,
    borderRadius: 6,
    background: "#FFFFFF",
    color: C.textSub,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  pageSize: {
    height: 24,
    padding: "0 4px",
    border: `1px solid ${C.inputBorder}`,
    borderRadius: 6,
    background: "#FFFFFF",
    color: C.text,
    fontFamily: "inherit",
    fontSize: 12,
    cursor: "pointer",
  },
};

/** Icon nét theo đúng đường vẽ của bản thiết kế */
const StrokeIcon = ({ paths, color, size, strokeWidth }: { paths: string[]; color: string; size: number; strokeWidth: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ flexShrink: 0 }}
  >
    {paths.map((d) => (
      <path key={d} d={d} />
    ))}
  </svg>
);

const SHIELD = "M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z";
const ICON_SHIELD_CHECK = [SHIELD, "M9 12l2 2 4-4"];
const ICON_SHIELD_PLUS = [SHIELD, "M12 9v6M9 12h6"];
const ICON_PENCIL = ["M4 20h4L19 9l-4-4L4 16z"];
const ICON_TRASH = ["M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"];
const ICON_SWAP = ["M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"];

/** Tên sản phẩm: nhãn BHMR cho thẻ, hậu tố cho hàng không serial; rê chuột hiện đủ tên (ô hẹp bị cắt "…") */
const ProductNameCell = ({ name, isCard, noSerial }: { name: string; isCard: boolean; noSerial: boolean }) => {
  const label = noSerial ? `${name} · không serial` : name;
  return (
    <span style={S.cell}>
      {isCard && <span style={S.badge}>BHMR</span>}
      <Tooltip label={label} multiline maw={360} openDelay={300} disabled={!label}>
        <span style={S.ellipsis}>{label}</span>
      </Tooltip>
    </span>
  );
};

interface WarrantyDeviceCellProps {
  row: ScannedSerialRow;
  disabled: boolean;
  onPick: (cardSerial: string) => void;
}

/** Serial thiết bị gắn với thẻ BHMR: "—" nếu không phải thẻ, chip xanh nếu đã gắn, nút chọn nếu chưa gắn */
const WarrantyDeviceCell = ({ row, disabled, onPick }: WarrantyDeviceCellProps) => {
  if (!row.isWarrantyCard) {
    return <span style={{ ...S.cell, color: C.empty }}>—</span>;
  }
  const cardSerial = String(row.serial ?? "");
  const linked = row.linkedDeviceSerial;
  if (linked) {
    const label = (
      <>
        <StrokeIcon paths={ICON_SHIELD_CHECK} color="currentColor" size={13} strokeWidth={2.2} />
        <span style={S.ellipsis}>{linked}</span>
      </>
    );
    if (row.linkReadOnly) {
      return (
        <span style={S.cell}>
          <Tooltip label="Gắn sẵn từ đơn bán, không đổi được">
            <span style={S.chip}>{label}</span>
          </Tooltip>
        </span>
      );
    }
    if (disabled) {
      return (
        <span style={S.cell}>
          <span style={S.chip}>{label}</span>
        </span>
      );
    }
    return (
      <span style={S.cell}>
        <Tooltip label="Đổi thiết bị">
          <button
            type="button"
            className="ssg-btn"
            style={{ ...S.chip, cursor: "pointer" }}
            aria-label={`Đổi thiết bị cho thẻ ${cardSerial}, đang gắn ${linked}`}
            onClick={() => onPick(cardSerial)}
          >
            {label}
          </button>
        </Tooltip>
      </span>
    );
  }
  return (
    <span style={S.cell}>
      <button
        type="button"
        className="ssg-btn"
        style={{ ...S.pick, ...disabledLook(disabled) }}
        disabled={disabled}
        aria-label={`Chọn thiết bị cho thẻ ${cardSerial}`}
        onClick={() => onPick(cardSerial)}
      >
        <StrokeIcon paths={ICON_SHIELD_PLUS} color="currentColor" size={13} strokeWidth={2.2} />
        Chọn thiết bị
      </button>
    </span>
  );
};

interface ExchangeSerialCellProps {
  row: ScannedSerialRow;
  disabled: boolean;
  onPick: (serial: string) => void;
}

/**
 * Xuất đổi trả: serial khách trả mà serial xuất này thay thế ("Serial nhập"). Chip nếu đã chọn, nút chọn nếu chưa;
 * chưa chọn thì backend tự ghép theo "Serial đổi" của dòng đơn hoặc cả đơn.
 */
const ExchangeSerialCell = ({ row, disabled, onPick }: ExchangeSerialCellProps) => {
  if (row.isNoSerial) {
    return <span style={{ ...S.cell, color: C.empty }}>—</span>;
  }
  const serial = String(row.serial ?? "");
  const chosen = row.exchangeOldSerials ?? [];
  if (chosen.length) {
    const text = chosen.join(", ");
    const label = (
      <>
        <StrokeIcon paths={ICON_SWAP} color="currentColor" size={13} strokeWidth={2.2} />
        <span style={S.ellipsis}>{text}</span>
      </>
    );
    if (disabled) {
      return (
        <span style={S.cell}>
          <Tooltip label={text}>
            <span style={S.chip}>{label}</span>
          </Tooltip>
        </span>
      );
    }
    return (
      <span style={S.cell}>
        <Tooltip label={`Đổi serial nhập · ${text}`}>
          <button
            type="button"
            className="ssg-btn"
            style={{ ...S.chip, cursor: "pointer" }}
            aria-label={`Đổi serial nhập cho ${serial}, đang chọn ${text}`}
            onClick={() => onPick(serial)}
          >
            {label}
          </button>
        </Tooltip>
      </span>
    );
  }
  return (
    <span style={S.cell}>
      <button
        type="button"
        className="ssg-btn"
        style={{ ...S.pick, ...disabledLook(disabled) }}
        disabled={disabled}
        aria-label={`Chọn serial nhập cho ${serial}`}
        onClick={() => onPick(serial)}
      >
        <StrokeIcon paths={ICON_SWAP} color="currentColor" size={13} strokeWidth={2.2} />
        Chọn serial nhập
      </button>
    </span>
  );
};

interface RowActionsProps {
  serial: string;
  hasChildren: boolean;
  disabled: boolean;
  onOpenChildren: () => void;
  onRemove: () => void;
}

/** Hai nút vuông: serial con và xoá dòng */
const RowActions = ({ serial, hasChildren, disabled, onOpenChildren, onRemove }: RowActionsProps) => {
  const childLabel = hasChildren ? "Thông tin serial con" : "Chưa có serial con";
  return (
    <span style={S.actions}>
      <Tooltip label={childLabel}>
        <button
          type="button"
          className="ssg-btn"
          style={{ ...S.iconButton, border: `1px solid ${C.childBorder}`, background: C.childBg }}
          aria-label={`${childLabel} ${serial}`}
          onClick={onOpenChildren}
        >
          <StrokeIcon paths={ICON_PENCIL} color={C.childIcon} size={14} strokeWidth={2} />
        </button>
      </Tooltip>
      <Tooltip label="Xóa">
        <button
          type="button"
          className="ssg-btn"
          style={{
            ...S.iconButton,
            border: `1px solid ${C.dangerBorder}`,
            background: C.dangerBg,
            ...disabledLook(disabled),
          }}
          disabled={disabled}
          aria-label={`Xóa ${serial}`}
          onClick={onRemove}
        >
          <StrokeIcon paths={ICON_TRASH} color={C.danger} size={14} strokeWidth={2} />
        </button>
      </Tooltip>
    </span>
  );
};

interface PageInfo {
  page: number;
  totalPages: number;
  rowCount: number;
}

interface GridFooterProps extends PageInfo {
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onGoToPage: (page: number) => void;
}

/** Chân lưới: phân trang 25 / 50 / 100 bên phải */
const GridFooter = ({ page, totalPages, rowCount, pageSize, onPageSizeChange, onGoToPage }: GridFooterProps) => {
  const from = rowCount > 0 ? page * pageSize + 1 : 0;
  const to = Math.min((page + 1) * pageSize, rowCount);
  const atFirst = page <= 0;
  const atLast = page >= totalPages - 1;
  return (
    <div style={S.footer}>
      <span style={{ ...S.pager, marginLeft: "auto" }}>
        <span>
          {from} – {to} / {rowCount}
        </span>
        {totalPages > 1 && (
          <span style={S.pageNav}>
            <button
              type="button"
              style={{ ...S.pageButton, ...disabledLook(atFirst) }}
              disabled={atFirst}
              aria-label="Trang đầu"
              onClick={() => onGoToPage(0)}
            >
              <IconChevronsLeft size={14} stroke={2} />
            </button>
            <button
              type="button"
              style={{ ...S.pageButton, ...disabledLook(atFirst) }}
              disabled={atFirst}
              aria-label="Trang trước"
              onClick={() => onGoToPage(page - 1)}
            >
              <IconChevronLeft size={14} stroke={2} />
            </button>
            <span style={{ padding: "0 4px" }}>
              Trang {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              style={{ ...S.pageButton, ...disabledLook(atLast) }}
              disabled={atLast}
              aria-label="Trang sau"
              onClick={() => onGoToPage(page + 1)}
            >
              <IconChevronRight size={14} stroke={2} />
            </button>
            <button
              type="button"
              style={{ ...S.pageButton, ...disabledLook(atLast) }}
              disabled={atLast}
              aria-label="Trang cuối"
              onClick={() => onGoToPage(totalPages - 1)}
            >
              <IconChevronsRight size={14} stroke={2} />
            </button>
          </span>
        )}
        <select
          aria-label="Số dòng mỗi trang"
          style={S.pageSize}
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
        >
          {paginationPageSizeSelector.map((size) => (
            <option key={size} value={size}>
              {size} / trang
            </option>
          ))}
        </select>
      </span>
    </div>
  );
};

export interface ScannedSerialGridHandlers {
  onRemove: (serials: string[]) => void;
  onQuantityChange: (serial: string, quantity: number) => void;
  onNoteChange: (serial: string, note: string | null) => void;
  onPrintAssembly: (row: ScannedSerialRow | undefined) => void;
  onImportExcel: () => void;
  onExportExcel: () => void;
  onSelectionChange: (rows: ScannedSerialRow[]) => void;
}

interface ScannedSerialGridProps extends ScannedSerialGridHandlers {
  rows: ScannedSerialRow[];
  orderLines: SaleOrderLine[];
  readOnly: boolean;
  locked: boolean;
  height: number;
  action: string;
  /** Mở hộp chọn thiết bị cho thẻ BHMR (serial thẻ) */
  onPickDevice: (cardSerial: string) => void;
  /** Xuất đổi trả: hiện cột "Serial nhập" (SALE_KIND_CONFIG.exchangeSerialColumn) */
  showExchangeColumn: boolean;
  /** Xuất đổi trả: mở hộp chọn serial khách trả cho một serial xuất */
  onPickExchangeSerial: (serial: string) => void;
  /** Serial cần cuộn tới và nháy dòng; đổi giá trị (khác null) mới chạy */
  focusSerial: string | null;
}

/**
 * Lưới serial đã quét.
 * - getRowId theo serial: AG Grid chỉ cập nhật dòng thay đổi thay vì dựng lại mọi dòng.
 * - columnDefs chỉ phụ thuộc chế độ xem/khoá; dữ liệu và callback đọc qua ref nên
 *   không dựng lại cấu hình cột ở mỗi lần quét.
 * - Thứ tự cột theo thiết kế; Tên vị trí đứng cạnh Vị trí, ĐVT đứng cạnh SL (theo yêu cầu). Các cột thiết kế
 *   không có (Tình trạng, Ghi chú tình trạng, Ghi chú) vẫn giữ, nằm ở phần cuộn ngang. SL2, ĐVT2 đã bỏ theo yêu cầu.
 */
const ScannedSerialGrid = (props: ScannedSerialGridProps) => {
  const { rows, readOnly, locked, height, action, focusSerial, showExchangeColumn } = props;
  const gridRef = React.useRef<AgGridReact<ScannedSerialRow>>(null);
  const latest = React.useRef(props);
  latest.current = props;
  const disabled = readOnly || locked;

  const [pageSize, setPageSize] = React.useState(paginationPageSize);
  const [pageInfo, setPageInfo] = React.useState<PageInfo>({ page: 0, totalPages: 0, rowCount: 0 });

  const openChildSerials = React.useCallback(
    (row: ScannedSerialRow) =>
      modals.openConfirmModal({
        title: <Title order={4}>Thông tin serial con</Title>,
        radius: "md",
        size: "90%",
        children: (
          <WarehouseOutWardSerialChildrenV2
            type="XUAT"
            WarehouseInWardSerialChildren={row as any}
            action={action}
          />
        ),
        confirmProps: { display: "none" },
        cancelProps: { display: "none" },
      }),
    [action],
  );

  const columnDefs = React.useMemo<ColDef<ScannedSerialRow>[]>(
    () => [
      {
        headerName: "STT",
        colId: "index",
        pinned: "left",
        // Không sắp xếp / lọc: bỏ chỗ dành cho icon để tiêu đề không bị cắt còn "S"
        sortable: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        width: 52,
        minWidth: 52,
        maxWidth: 52,
        cellStyle: CELL_STYLE.index,
        valueGetter: (params) => Number(params?.node?.rowIndex) + 1,
      },
      {
        field: "positionCode",
        headerName: "Vị trí",
        headerTooltip: "Mã vị trí",
        filter: "agTextColumnFilter",
        width: 84,
        cellStyle: CELL_STYLE.sub,
      },
      {
        field: "positionName",
        headerName: "Tên vị trí",
        filter: "agTextColumnFilter",
        width: 150,
        cellStyle: CELL_STYLE.sub,
      },
      { field: "itemCode", headerName: "Mã hàng", filter: "agTextColumnFilter", width: 116 },
      {
        field: "serial",
        headerName: "Serial",
        filter: "agTextColumnFilter",
        width: 128,
        cellStyle: CELL_STYLE.serial,
      },
      {
        field: "itemName",
        headerName: "Tên sản phẩm",
        filter: "agTextColumnFilter",
        flex: 1,
        minWidth: 160,
        cellRenderer: (params: ICellRendererParams<ScannedSerialRow>) =>
          params.data ? (
            <ProductNameCell
              name={params.data.itemName ?? ""}
              isCard={params.data.isWarrantyCard === true}
              noSerial={isNoSerialRow(latest.current.orderLines, params.data)}
            />
          ) : null,
      },
      {
        field: "primaryQuantity",
        headerName: "SL",
        type: "rightAligned",
        // Đủ chỗ cho chữ + icon sắp xếp / lọc
        width: 72,
        filter: "agNumberColumnFilter",
        cellEditor: "agNumberCellEditor",
        singleClickEdit: true,
        // Chỉ hàng không quản lý serial mới sửa được số lượng
        editable: (params) => !disabled && !!params.data && isNoSerialRow(latest.current.orderLines, params.data),
        cellEditorParams: (params: ICellRendererParams<ScannedSerialRow>) => {
          const line = params.data ? findOrderLineForRow(latest.current.orderLines, params.data) : undefined;
          return { min: 1, max: line?.primaryQuantity };
        },
        // Ô sửa được hiện dạng ô nhập như thiết kế; bấm một lần để sửa
        cellRenderer: (params: ICellRendererParams<ScannedSerialRow>) =>
          !disabled && params.data && isNoSerialRow(latest.current.orderLines, params.data) ? (
            <span style={S.qtyCell}>
              <span style={S.qtyBox}>{params.value}</span>
            </span>
          ) : (
            params.value ?? null
          ),
      },
      { field: "primaryUomCode", headerName: "ĐVT", filter: "agTextColumnFilter", width: 80 },
      {
        colId: "salePrice",
        headerName: "Giá bán",
        type: "rightAligned",
        width: 100,
        valueGetter: (params) => formatMoney(params.data?.soPrice),
      },
      {
        colId: "totalAmount",
        headerName: "Thành tiền",
        type: "rightAligned",
        width: 104,
        valueGetter: (params) =>
          formatMoney((Number(params.data?.soPrice) || 0) * (Number(params.data?.primaryQuantity) || 0)),
      },
      { field: "serialStatus", headerName: "Tình trạng", filter: "agTextColumnFilter", width: 130 },
      { field: "attribute11", headerName: "Ghi chú tình trạng", filter: "agTextColumnFilter", width: 190 },
      {
        field: "note",
        headerName: "Ghi chú",
        editable: !disabled,
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        width: 200,
      },
      // Xuất đổi trả: serial khách trả mà serial xuất thay thế; ghim phải cạnh cột BH như thiết kế
      ...(showExchangeColumn
        ? [
            {
              colId: "exchangeOldSerials",
              headerName: "Serial nhập",
              pinned: "right",
              headerClass: "ssg-head-bh",
              filter: "agTextColumnFilter",
              width: 150,
              cellStyle: CELL_STYLE.warranty,
              suppressKeyboardEvent: suppressButtonKeys,
              valueGetter: (params: ValueGetterParams<ScannedSerialRow>) =>
                (params.data?.exchangeOldSerials ?? []).join(", "),
              cellRenderer: (params: ICellRendererParams<ScannedSerialRow>) =>
                params.data ? (
                  <ExchangeSerialCell
                    row={params.data}
                    disabled={disabled}
                    onPick={(serial) => latest.current.onPickExchangeSerial(serial)}
                  />
                ) : null,
            } as ColDef<ScannedSerialRow>,
          ]
        : []),
      {
        colId: "linkedDeviceSerial",
        headerName: "Serial thiết bị BH",
        // Ghim phải sát Thao tác để luôn thấy như thiết kế; các cột thêm cuộn ở giữa
        pinned: "right",
        headerClass: "ssg-head-bh",
        filter: "agTextColumnFilter",
        width: 150,
        cellStyle: CELL_STYLE.warranty,
        suppressKeyboardEvent: suppressButtonKeys,
        valueGetter: (params) => (params.data?.isWarrantyCard ? params.data.linkedDeviceSerial ?? "" : ""),
        cellRenderer: (params: ICellRendererParams<ScannedSerialRow>) =>
          params.data ? (
            <WarrantyDeviceCell
              row={params.data}
              disabled={disabled}
              onPick={(cardSerial) => latest.current.onPickDevice(cardSerial)}
            />
          ) : null,
      },
      {
        colId: "action",
        headerName: "Thao tác",
        headerClass: "ssg-head-center",
        pinned: "right",
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        suppressKeyboardEvent: suppressButtonKeys,
        cellRenderer: (params: ICellRendererParams<ScannedSerialRow>) => {
          const row = params.data;
          if (!row) return null;
          const serial = String(row.serial ?? "");
          return (
            <RowActions
              serial={serial}
              hasChildren={(row.tblInventoryChildSerialOnhandCommands?.length ?? 0) > 0}
              disabled={disabled}
              onOpenChildren={() => openChildSerials(row)}
              onRemove={() => serial && latest.current.onRemove([serial])}
            />
          );
        },
      },
    ],
    [disabled, openChildSerials, showExchangeColumn],
  );

  const onCellValueChanged = React.useCallback((event: CellValueChangedEvent<ScannedSerialRow>) => {
    const serial = event.data?.serial;
    if (!serial) return;
    const colId = event.column.getColId();
    if (colId === "primaryQuantity") {
      const quantity = Number(event.newValue);
      const line = findOrderLineForRow(latest.current.orderLines, event.data);
      if (!quantity || Number.isNaN(quantity) || quantity < 1) {
        NotificationExtension.Warn("Số lượng không hợp lệ.");
        event.node.setDataValue("primaryQuantity", event.oldValue);
        return;
      }
      if (line && quantity > Number(line.primaryQuantity)) {
        NotificationExtension.Warn("Số lượng vượt quá số lượng cần xuất");
        event.node.setDataValue("primaryQuantity", event.oldValue);
        return;
      }
      latest.current.onQuantityChange(serial, quantity);
    }
    if (colId === "note") {
      latest.current.onNoteChange(serial, event.newValue ?? null);
    }
  }, []);

  const getContextMenuItems = React.useCallback(
    (params: GetContextMenuItemsParams<ScannedSerialRow>): (DefaultMenuItem | MenuItemDef)[] => {
      const rowNumber = Number(params?.node?.rowIndex) + 1 || "";
      const { readOnly, locked } = latest.current;
      return [
        {
          name: `Xóa dòng thứ: ${rowNumber}`,
          disabled: readOnly || locked,
          action: () => params.node?.data?.serial && latest.current.onRemove([params.node.data.serial]),
          cssClasses: ["red", "bold"],
        },
        "separator",
        {
          name: "In linh kiện lắp ráp",
          action: () => latest.current.onPrintAssembly(params.node?.data),
          cssClasses: ["red", "bold"],
        },
        {
          name: "Import Excel",
          disabled: readOnly || locked,
          action: () => latest.current.onImportExcel(),
          cssClasses: ["red", "bold"],
        },
        {
          name: "Export Excel",
          action: () => latest.current.onExportExcel(),
          cssClasses: ["red", "bold"],
        },
        "separator",
        "copy",
        "copyWithHeaders",
        "copyWithGroupHeaders",
      ];
    },
    [],
  );

  const getRowId = React.useCallback((params: GetRowIdParams<ScannedSerialRow>) => String(params.data.serial), []);

  // Nền dòng: chỉ tô hàng không serial. Thẻ BHMR là sản phẩm bình thường nên để nền trắng như các dòng khác
  const getRowStyle = React.useCallback((params: RowClassParams<ScannedSerialRow>): RowStyle | undefined => {
    const row = params.data;
    if (!row) return undefined;
    if (isNoSerialRow(latest.current.orderLines, row)) return { backgroundColor: C.noSerialRow };
    return undefined;
  }, []);

  // STT lấy theo rowIndex: làm mới sau khi xoá / sắp xếp / lọc để số thứ tự không bị lệch
  const onModelUpdated = React.useCallback(
    (event: ModelUpdatedEvent<ScannedSerialRow>) => event.api.refreshCells({ columns: ["index"] }),
    [],
  );

  const onPaginationChanged = React.useCallback((event: PaginationChangedEvent<ScannedSerialRow>) => {
    const next: PageInfo = {
      page: event.api.paginationGetCurrentPage(),
      totalPages: event.api.paginationGetTotalPages(),
      rowCount: event.api.paginationGetRowCount(),
    };
    setPageInfo((prev) =>
      prev.page === next.page && prev.totalPages === next.totalPages && prev.rowCount === next.rowCount ? prev : next,
    );
  }, []);

  const onSelectionChanged = React.useCallback(
    (event: SelectionChangedEvent<ScannedSerialRow>) => latest.current.onSelectionChange(event.api.getSelectedRows()),
    [],
  );

  const goToPage = React.useCallback((page: number) => gridRef.current?.api.paginationGoToPage(page), []);

  // Tới dòng được yêu cầu: chuyển trang chứa dòng, cuộn ra giữa rồi nháy dòng.
  // Chạy sau một nhịp để lưới nhận xong rowData mới (dòng vừa quét).
  React.useEffect(() => {
    if (!focusSerial) return undefined;
    let flashTimer: number | undefined;
    const locateTimer = window.setTimeout(() => {
      const api = gridRef.current?.api;
      const node = api?.getRowNode(focusSerial);
      if (!api || !node || node.rowIndex == null) return;
      const page = Math.floor(node.rowIndex / api.paginationGetPageSize());
      if (page !== api.paginationGetCurrentPage()) api.paginationGoToPage(page);
      api.ensureNodeVisible(node, "middle");
      // flashCells chỉ tác động lên ô đã render, nên đợi lưới vẽ xong sau khi cuộn
      flashTimer = window.setTimeout(
        () => api.flashCells({ rowNodes: [node], flashDuration: 800, fadeDuration: 1000 }),
        120,
      );
    }, 0);
    return () => {
      window.clearTimeout(locateTimer);
      window.clearTimeout(flashTimer);
    };
  }, [focusSerial]);

  return (
    <div style={{ ...S.wrapper, height: height - 100 }}>
      <div style={S.grid}>
        <AgGridReact<ScannedSerialRow>
          ref={gridRef}
          rowData={rows}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          theme={GRID_THEME}
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          cellSelection={true}
          onCellValueChanged={onCellValueChanged}
          onModelUpdated={onModelUpdated}
          pagination={true}
          paginationPageSize={pageSize}
          suppressPaginationPanel={true}
          onPaginationChanged={onPaginationChanged}
          localeText={AG_GRID_LOCALE_VN}
          rowSelection={{ mode: "multiRow" }}
          onSelectionChanged={onSelectionChanged}
          getContextMenuItems={getContextMenuItems}
          suppressRowHoverHighlight={true}
          columnHoverHighlight={true}
          suppressMenuHide={false}
        />
      </div>
      <GridFooter
        page={pageInfo.page}
        totalPages={pageInfo.totalPages}
        rowCount={pageInfo.rowCount}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        onGoToPage={goToPage}
      />
    </div>
  );
};

export default React.memo(ScannedSerialGrid);
